import React, { useState, useEffect } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, TextField, Tooltip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import sellingResourceApi from '../../services/sellingResourceApi';
import usePermissions from '../../hooks/usePermissions';
import { apiError, apiSuccess } from '../../utils/toast';

const emptyForm = () => ({ resourceName: '', resourceTypeId: '', description: '' });

/**
 * "Quick Create Selling Resource" modal. Used by the QuickCreate component when
 * entityKey === 'sellingresource'. Resource code is auto-generated by the backend
 * (SR-######), so only Name/Type/Description are collected here.
 */
const QuickCreateSellingResource = ({ onCreated, disabled = false, size = 'small', tooltip }) => {
  const { hasPermission, loading: permLoading } = usePermissions();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const res = await sellingResourceApi.getTypes({ page: 1, limit: 200 });
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        if (mounted) setTypes(list);
      } catch {
        if (mounted) setTypes([]);
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  if (permLoading) return null;
  if (!hasPermission('sellingresource.create')) return null;

  const openDialog = () => {
    setForm(emptyForm());
    setErrors({});
    setOpen(true);
  };
  const closeDialog = () => { if (!saving) setOpen(false); };
  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const next = {};
    if (!String(form.resourceName || '').trim()) next.resourceName = 'Resource name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        resourceName: String(form.resourceName).trim(),
        resourceTypeId: form.resourceTypeId || null,
        description: form.description ? String(form.description).trim() : null,
      };
      const result = await sellingResourceApi.create(payload);
      const created = result?.data || result;
      apiSuccess('Selling resource created successfully.');
      setOpen(false);
      if (onCreated && created?.id) onCreated(created);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create selling resource.';
      apiError(typeof msg === 'string' ? msg : 'Failed to create selling resource.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip title={tooltip || 'Quick Create Selling Resource'}>
        <span>
          <IconButton
            size={size}
            color="primary"
            onClick={openDialog}
            disabled={disabled}
            aria-label="Quick Create Selling Resource"
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Quick Create Selling Resource</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Resource Name"
              value={form.resourceName}
              onChange={(e) => set('resourceName', e.target.value)}
              fullWidth
              size="small"
              required
              error={!!errors.resourceName}
              helperText={errors.resourceName}
              autoFocus
            />
            <TextField
              label="Resource Type"
              select
              value={form.resourceTypeId}
              onChange={(e) => set('resourceTypeId', e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">None</MenuItem>
              {types.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.typeName}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickCreateSellingResource;
